'use client';

import { useEffect, useState } from 'react';

/**
 * varying height of the body based on the open menu.
 * This is used to make sure the iframe expands effectively 'pushing' the content on the parent page
 * even when the menu itself is absolute positioned (which normally wouldn't trigger size changes).
 */
export function InjectableMenuSpacer() {
  useEffect(() => {
    // We need to observe the DOM for changes in the navigation menu
    // Radix UI adds/removes elements or changes attributes when menus open

    // The viewport is what usually animates height for the MegaMenu
    // But the user menu might just be a Popover/Dropdown

    let lastModalState = false;

    const updateHeight = () => {
      // Find all potential open menu content containers
      // Radix NavigationMenu Viewport
      const viewport = document.querySelector('[data-slot="navigation-menu-viewport"]');

      // Radix Dropdown/Popover Content (User Menu)
      // These often get appended to the body, but in an iframe they are in our document.html
      // We look for any visible content with data-state="open"
      // Exclude buttons to avoid measuring triggers, focus on the content
      const openContents = document.querySelectorAll('[data-state="open"]:not(button)');

      let maxBottom = 0;

      if (viewport) {
        const rect = viewport.getBoundingClientRect();
        // If viewport has height (it animates), use it
        if (rect.height > 0) {
          maxBottom = Math.max(maxBottom, rect.bottom);
        }
      }

      openContents.forEach((el) => {
        const rect = el.getBoundingClientRect();
        maxBottom = Math.max(maxBottom, rect.bottom);
      });

      // Default header height
      const headerHeight = 72;

      if (maxBottom > headerHeight) {
        // Add some padding
        document.body.style.minHeight = `${maxBottom + 20}px`;
      } else {
        document.body.style.minHeight = '';
      }

      // Check for open modal (Dialog)
      // Must check visibility because hidden modals (like cookie consent) might exist in DOM
      const modals = document.querySelectorAll('[role="dialog"], [data-slot="dialog-content"]');
      const isModalOpen = Array.from(modals).some((modal) => {
        const style = window.getComputedStyle(modal);
        return style.display !== 'none' && style.visibility !== 'hidden';
      });

      // Only send modal message when state actually changes to avoid spamming
      if (window.parent && isModalOpen !== lastModalState) {
        lastModalState = isModalOpen;
        window.parent.postMessage(
          {
            type: 'zappingai-modal',
            open: isModalOpen,
          },
          '*'
        );
      }
    };

    // Observer to watch for attribute changes (animation states) and child list changes (mounting content)
    const observer = new MutationObserver((mutations) => {
      updateHeight();
      // Also update after a short delay to allow for animations to start/progress
      requestAnimationFrame(updateHeight);
      setTimeout(updateHeight, 150);
      setTimeout(updateHeight, 300);
    });

    observer.observe(document.body, {
      attributes: true,
      childList: true,
      subtree: true,
      attributeFilter: ['data-state', 'style', 'class'],
    });

    return () => observer.disconnect();
  }, []);

  return null;
}
